import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Shield, Save, RotateCcw } from 'lucide-react'
import { Permission } from '@/lib/rolePermissions'

interface Organization {
  id: string;
  name: string;
}

interface RolesPermissionsTabProps {
  organization: Organization;
  onSave: (data: any) => void;
}

export function RolesPermissionsTab({ organization, onSave }: RolesPermissionsTabProps) {
  const [permissions, setPermissions] = useState({
    admin: {
      [Permission.UPLOAD_DOCUMENTS]: true,
      [Permission.CREATE_COMMENTS]: true,
      [Permission.PROPOSE_DECISIONS]: true,
      [Permission.CHANGE_FINDING_STATUS]: true,
      [Permission.MODULE_SIGNOFF]: true,
      [Permission.FINAL_CASE_SIGNOFF]: true,
      [Permission.EXPORT_PREMIUM_REPORT]: true,
      [Permission.GENERATE_REPORT]: true,
    },
    moderator: {
      [Permission.UPLOAD_DOCUMENTS]: true,
      [Permission.CREATE_COMMENTS]: true,
      [Permission.PROPOSE_DECISIONS]: true,
      [Permission.CHANGE_FINDING_STATUS]: true,
      [Permission.MODULE_SIGNOFF]: true,
      [Permission.FINAL_CASE_SIGNOFF]: false,
      [Permission.EXPORT_PREMIUM_REPORT]: false,
      [Permission.GENERATE_REPORT]: false,
    },
    user: {
      [Permission.UPLOAD_DOCUMENTS]: true,
      [Permission.CREATE_COMMENTS]: true,
      [Permission.PROPOSE_DECISIONS]: true,
      [Permission.CHANGE_FINDING_STATUS]: false,
      [Permission.MODULE_SIGNOFF]: false,
      [Permission.FINAL_CASE_SIGNOFF]: false,
      [Permission.EXPORT_PREMIUM_REPORT]: false,
      [Permission.GENERATE_REPORT]: false,
    }
  })

  const permissionLabels = {
    [Permission.UPLOAD_DOCUMENTS]: 'Ladda upp dokument',
    [Permission.CREATE_COMMENTS]: 'Skapa kommentarer',
    [Permission.PROPOSE_DECISIONS]: 'Föreslå beslut',
    [Permission.CHANGE_FINDING_STATUS]: 'Ändra status på fynd',
    [Permission.MODULE_SIGNOFF]: 'Godkänna moduler',
    [Permission.FINAL_CASE_SIGNOFF]: 'Slutgodkänna ärenden',
    [Permission.EXPORT_PREMIUM_REPORT]: 'Exportera premiumrapporter',
    [Permission.GENERATE_REPORT]: 'Generera rapporter'
  }

  const roleLabels = {
    admin: 'Admin/Partner',
    moderator: 'Senior Revisor',
    user: 'Junior Revisor'
  }

  const handlePermissionChange = (role: string, permission: Permission, enabled: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role as keyof typeof prev],
        [permission]: enabled
      }
    }))
  }

  const resetToDefaults = () => {
    setPermissions({
      admin: {
        [Permission.UPLOAD_DOCUMENTS]: true,
        [Permission.CREATE_COMMENTS]: true,
        [Permission.PROPOSE_DECISIONS]: true,
        [Permission.CHANGE_FINDING_STATUS]: true,
        [Permission.MODULE_SIGNOFF]: true,
        [Permission.FINAL_CASE_SIGNOFF]: true,
        [Permission.EXPORT_PREMIUM_REPORT]: true,
        [Permission.GENERATE_REPORT]: true,
      },
      moderator: {
        [Permission.UPLOAD_DOCUMENTS]: true,
        [Permission.CREATE_COMMENTS]: true,
        [Permission.PROPOSE_DECISIONS]: true,
        [Permission.CHANGE_FINDING_STATUS]: true,
        [Permission.MODULE_SIGNOFF]: true,
        [Permission.FINAL_CASE_SIGNOFF]: false,
        [Permission.EXPORT_PREMIUM_REPORT]: false,
        [Permission.GENERATE_REPORT]: false,
      },
      user: {
        [Permission.UPLOAD_DOCUMENTS]: true,
        [Permission.CREATE_COMMENTS]: true,
        [Permission.PROPOSE_DECISIONS]: true,
        [Permission.CHANGE_FINDING_STATUS]: false,
        [Permission.MODULE_SIGNOFF]: false,
        [Permission.FINAL_CASE_SIGNOFF]: false,
        [Permission.EXPORT_PREMIUM_REPORT]: false,
        [Permission.GENERATE_REPORT]: false,
      }
    })
  }

  const handleSave = () => {
    onSave({ permissions })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Behörighetsmatris
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Konfigurera vilka behörigheter som tillhör respektive roll
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Behörighet</th>
                  {Object.entries(roleLabels).map(([role, label]) => (
                    <th key={role} className="text-center p-3 font-medium min-w-32">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.values(Permission).map((permission) => (
                  <tr key={permission} className="border-b hover:bg-muted/50">
                    <td className="p-3">
                      <Label className="font-medium">
                        {permissionLabels[permission]}
                      </Label>
                    </td>
                    {Object.keys(roleLabels).map((role) => (
                      <td key={role} className="p-3 text-center">
                        <Switch
                          checked={permissions[role as keyof typeof permissions][permission]}
                          onCheckedChange={(checked) => 
                            handlePermissionChange(role, permission, checked)
                          }
                          disabled={role === 'admin' && [
                            Permission.FINAL_CASE_SIGNOFF,
                            Permission.EXPORT_PREMIUM_REPORT,
                            Permission.GENERATE_REPORT
                          ].includes(permission)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <Button
              variant="outline"
              onClick={resetToDefaults}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Återställ standard
            </Button>
            
            <Button onClick={handleSave} className="gap-2">
              <Save className="h-4 w-4" />
              Spara behörigheter
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rollbeskrivningar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium text-sm">Junior Revisor</h4>
            <p className="text-sm text-muted-foreground">
              Grundläggande behörigheter för att ladda upp dokument och delta i granskningsprocessen.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-sm">Senior Revisor</h4>
            <p className="text-sm text-muted-foreground">
              Utökade behörigheter för att granska och godkänna moduler samt ändra status på fynd.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-sm">Admin/Partner</h4>
            <p className="text-sm text-muted-foreground">
              Fullständiga behörigheter inklusive slutgodkännande och export av premiumrapporter.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}